import React, { cloneElement, isValidElement, useCallback, useEffect, useState } from "react";
import classNames from "classnames";
import GlobalOuterLayout from "components/routes/global-outer-layout/GlobalOuterLayout";
import GlobalHeader from "components/v2/global-header/GlobalHeader";
import SideBar from "components/v2/sidebar/SideBar";
import Footer from "components/footer/Footer";
import { NotificationProvider } from "contexts/NotificationContext";
import useDesktopChecker from "hooks/useDesktopChecker";
import useTabletChecker from "hooks/useTabletChecker";
import TabletNavOverlay from "components/v2/tablet-nav-overlay/TabletNavOverlay";
import { setShellLayoutAttributes } from "new-relic/new-relic";
import {
  type ShellAnalyticsContext,
  type ShellContext,
  type ShellViewport,
  tagFSShellLayoutSession,
  trackFSNavShellSidebarOpen,
} from "utilities/full-story";
import { getShellSessionFromStorage } from "utilities/shellAnalytics";
import ShellSpinner from "./ShellSpinner";
import { useLockBackNavigation } from "./useLockBackNavigation";
import "./globalNavigationShell.scss";

export type NavSlotPlacement = "sidebar" | "sidebar-bottom" | "none";

export interface GlobalNavigationShellProps {
  readonly notificationsEnabled: boolean;
  readonly children: React.ReactNode;
  readonly navSlot?: React.ReactNode;
  readonly navSlotPlacement?: NavSlotPlacement;
  readonly contentHeaderSlot?: React.ReactNode;
  /** Bottom navigation frame for mobile viewports (e.g. MobileBottomNav). */
  readonly mobileNavSlot?: React.ReactNode;
  readonly hideFooter?: boolean;
  readonly hideHeader?: boolean;
  readonly hideNotifications?: boolean;
  readonly lockBackNavigation?: boolean;
  readonly onNotificationsClick?: () => void;
  readonly onDocumentNotificationClick?: (documentId: string) => void;
  readonly mobileContentClassName?: string;
  /** When true, renders ShellSpinner over shell content. Caller supplies value; shell does not fetch data. */
  readonly isLoading?: boolean;
  /** Identifies the shell consumer for analytics (US-13). */
  readonly shellContext?: ShellContext;
  /** Optional user/loan context merged into shell analytics events (US-13). */
  readonly shellAnalyticsContext?: ShellAnalyticsContext;
}

interface GlobalNavigationShellContentProps {
  readonly children: React.ReactNode;
  readonly navSlot?: React.ReactNode;
  readonly navSlotPlacement: NavSlotPlacement;
  readonly contentHeaderSlot?: React.ReactNode;
  readonly mobileNavSlot?: React.ReactNode;
  readonly hideFooter?: boolean;
  readonly hideHeader?: boolean;
  readonly hideNotifications?: boolean;
  readonly lockBackNavigation?: boolean;
  readonly onNotificationsClick?: () => void;
  readonly onDocumentNotificationClick?: (documentId: string) => void;
  readonly mobileContentClassName?: string;
  readonly isLoading?: boolean;
  readonly shellContext?: ShellContext;
  readonly shellAnalyticsContext?: ShellAnalyticsContext;
}

const GlobalNavigationShellContent: React.FC<GlobalNavigationShellContentProps> = ({
  children,
  navSlot,
  navSlotPlacement,
  contentHeaderSlot,
  mobileNavSlot,
  hideFooter = false,
  hideHeader = false,
  hideNotifications = false,
  lockBackNavigation = false,
  onNotificationsClick,
  onDocumentNotificationClick,
  mobileContentClassName,
  isLoading = false,
  shellContext,
  shellAnalyticsContext,
}) => {
  const [isNavOpen, setIsNavOpen] = useState(false);
  const isDesktop = useDesktopChecker();
  const isTablet = useTabletChecker();

  useLockBackNavigation(lockBackNavigation);

  useEffect(() => {
    tagFSShellLayoutSession(shellContext);
    setShellLayoutAttributes(shellContext);
  }, [shellContext]);

  const buildShellAnalyticsProps = useCallback(
    (viewport: ShellViewport) => ({
      ...getShellSessionFromStorage(),
      ...shellAnalyticsContext,
      shellContext,
      viewport,
    }),
    [shellAnalyticsContext, shellContext],
  );

  const handleSetIsNavOpen = useCallback(
    (open: boolean) => {
      if (open && !isNavOpen && isTablet) {
        trackFSNavShellSidebarOpen(buildShellAnalyticsProps("tablet"));
      }
      setIsNavOpen(open);
    },
    [buildShellAnalyticsProps, isNavOpen, isTablet],
  );

  const renderedNavSlot = isValidElement(navSlot)
    ? cloneElement(navSlot as React.ReactElement<Record<string, unknown>>, {
        isNavOpen,
        setIsNavOpen: handleSetIsNavOpen,
        isDesktop,
        isTablet,
      })
    : navSlot;

  const showNavInSidebar = Boolean(navSlot) && navSlotPlacement !== "none";

  const headerProps = {
    isNavOpen,
    setIsNavOpen: showNavInSidebar ? handleSetIsNavOpen : undefined,
    hideNotifications,
    onNotificationsClick,
    onDocumentNotificationClick,
  };

  const renderFooter = () => (!hideFooter ? <Footer /> : null);

  if (isDesktop) {
    return (
      <GlobalOuterLayout>
        <ShellSpinner isLoading={isLoading} />
        <SideBar>
          {showNavInSidebar && (
            <div className={`global-navigation-shell__nav-slot global-navigation-shell__nav-slot--${navSlotPlacement}`}>
              {renderedNavSlot}
            </div>
          )}
        </SideBar>
        <div className="global-outer-layout__wrapper">
          {!hideHeader && <GlobalHeader {...headerProps} />}
          {contentHeaderSlot && <div className="global-navigation-shell__content-header">{contentHeaderSlot}</div>}
          <main className="global-navigation-shell__content">{children}
          {renderFooter()}
          </main>
        </div>
      </GlobalOuterLayout>
    );
  }

  return (
    <GlobalOuterLayout>
      <ShellSpinner isLoading={isLoading} />
      <div className="global-navigation-shell__mobile-container">
        {!hideHeader && <GlobalHeader {...headerProps} />}
        <div
          className={classNames("global-navigation-shell__mobile-content", mobileContentClassName, {
            "global-navigation-shell__mobile-content--no-header": hideHeader,
            "global-navigation-shell__mobile-content--with-bottom-nav": Boolean(mobileNavSlot),
          })}
        >
          {contentHeaderSlot && <div className="global-navigation-shell__content-header">{contentHeaderSlot}</div>}
          <main className="global-navigation-shell__content">{children}</main>
          {renderFooter()}
        </div>
        {isTablet && showNavInSidebar && (
          <TabletNavOverlay isOpen={isNavOpen} onClose={() => handleSetIsNavOpen(false)}>
            <div className={`global-navigation-shell__nav-slot global-navigation-shell__nav-slot--${navSlotPlacement}`}>
              {renderedNavSlot}
            </div>
          </TabletNavOverlay>
        )}
        {!isTablet && mobileNavSlot}
      </div>
    </GlobalOuterLayout>
  );
};

const GlobalNavigationShell: React.FC<GlobalNavigationShellProps> = ({
  notificationsEnabled,
  children,
  navSlot,
  navSlotPlacement = "sidebar",
  contentHeaderSlot,
  mobileNavSlot,
  hideFooter,
  hideHeader,
  hideNotifications,
  lockBackNavigation,
  onNotificationsClick,
  onDocumentNotificationClick,
  mobileContentClassName,
  isLoading = false,
  shellContext,
  shellAnalyticsContext,
}) => {
  return (
    <NotificationProvider enabled={notificationsEnabled}>
      <GlobalNavigationShellContent
        navSlot={navSlot}
        navSlotPlacement={navSlotPlacement}
        contentHeaderSlot={contentHeaderSlot}
        mobileNavSlot={mobileNavSlot}
        hideFooter={hideFooter}
        hideHeader={hideHeader}
        hideNotifications={hideNotifications}
        lockBackNavigation={lockBackNavigation}
        onNotificationsClick={onNotificationsClick}
        onDocumentNotificationClick={onDocumentNotificationClick}
        mobileContentClassName={mobileContentClassName}
        isLoading={isLoading}
        shellContext={shellContext}
        shellAnalyticsContext={shellAnalyticsContext}
      >
        {children}
      </GlobalNavigationShellContent>
    </NotificationProvider>
  );
};

export default GlobalNavigationShell;
